import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Form,
  Input,
  Button,
  Table,
  Modal,
  message,
  Card,
  Switch,
  Select,
  Tag,
  Space,
  Popconfirm
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  EnvironmentOutlined
} from '@ant-design/icons';
import './PincodeManagement.css';
import { API_BASE_URL } from '../components/l1';

const { Option } = Select;

const PincodeManagement = () => {
  const [form] = Form.useForm();
  const [pincodes, setPincodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingPincode, setEditingPincode] = useState(null);
  const [searchText, setSearchText] = useState('');

 

  useEffect(() => {
    fetchPincodes();
  }, []);

  const fetchPincodes = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/pincodes`);
      setPincodes(response.data.data);
    } catch (error) {
      message.error('Failed to fetch pincodes');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    try {
      // Convert deliveryDays to number if it's a string but looks like number
      const processedValues = {
        ...values,
        deliveryDays: isNaN(values.deliveryDays) ? values.deliveryDays : Number(values.deliveryDays)
      };

      if (editingPincode) {
        await axios.put(`${API_BASE_URL}/pincodes/${editingPincode.pincode}`, processedValues);
        message.success('Pincode updated successfully');
      } else {
        await axios.post(`${API_BASE_URL}/pincodes`, processedValues);
        message.success('Pincode created successfully');
      }
      
      setModalVisible(false);
      form.resetFields();
      setEditingPincode(null);
      fetchPincodes();
    } catch (error) {
      message.error(error.response?.data?.message || 'Operation failed');
    }
  };

  const handleEdit = (record) => {
    setEditingPincode(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleDelete = async (pincode) => {
    try {
      await axios.delete(`${API_BASE_URL}/pincodes/${pincode}`);
      message.success('Pincode deleted successfully');
      fetchPincodes();
    } catch (error) {
      message.error('Failed to delete pincode');
    }
  };

  const filteredPincodes = pincodes.filter(pincode =>
    pincode.pincode.toLowerCase().includes(searchText.toLowerCase()) ||
    pincode.city.toLowerCase().includes(searchText.toLowerCase())
  );

  const columns = [
    {
      title: 'Pincode',
      dataIndex: 'pincode',
      key: 'pincode',
      sorter: (a, b) => a.pincode.localeCompare(b.pincode),
      render: (text) => (
        <div className="flex items-center gap-2">
          <EnvironmentOutlined className="text-blue-500" />
          <span className="font-mono font-bold">{text}</span>
        </div>
      ),
    },
    {
      title: 'City',
      dataIndex: 'city',
      key: 'city',
      sorter: (a, b) => a.city.localeCompare(b.city),
    },
    {
      title: 'Delivery Available',
      dataIndex: 'deliveryAvailable',
      key: 'deliveryAvailable',
      render: (available) => (
        <Tag color={available ? 'green' : 'red'} className="font-medium">
          {available ? 'Yes' : 'No'}
        </Tag>
      ),
    },
    {
      title: 'Delivery Days',
      dataIndex: 'deliveryDays',
      key: 'deliveryDays',
      render: (days) => (
        <div className="flex items-center gap-2">
          <Tag color={
            days === 'same' || days === 0 ? 'blue' :
            days === 1 ? 'green' :
            days === 2 ? 'orange' :
            days === 3 ? 'purple' : 'gray'
          } className="font-medium min-w-16 text-center">
            {typeof days === 'number' ? `${days} day${days !== 1 ? 's' : ''}` : days}
          </Tag>
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
            className="bg-blue-500 hover:bg-blue-600"
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete this pincode?"
            description="Are you sure you want to delete this pincode?"
            onConfirm={() => handleDelete(record.pincode)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-3">
            <EnvironmentOutlined className="text-4xl text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-800">Pincode Management</h1>
          </div>
          <p className="text-gray-600">
            Manage delivery pincodes with only 4 essential fields
          </p>
        </div>

        {/* Stats Card */}
        <div className="mb-6">
          <Card className="text-center shadow-sm">
            <div className="text-lg">
              <span className="font-bold text-blue-600">{pincodes.length}</span> pincodes managed
            </div>
          </Card>
        </div>

        {/* Search and Add Button */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
          <div className="w-full md:w-1/3">
            <Input
              placeholder="Search by pincode or city..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              size="large"
              className="w-full"
              allowClear
            />
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingPincode(null);
              form.resetFields();
              setModalVisible(true);
            }}
            size="large"
            className="bg-blue-600 hover:bg-blue-700 w-full md:w-auto"
          >
            Add New Pincode
          </Button>
        </div>

        {/* Table */}
        <Card className="shadow-md">
          <Table
            columns={columns}
            dataSource={filteredPincodes}
            rowKey="pincode"
            loading={loading}
            pagination={{ pageSize: 10 }}
            scroll={{ x: true }}
            className="responsive-table"
          />
        </Card>

        {/* Modal for Add/Edit */}
        <Modal
          title={editingPincode ? 'Edit Pincode' : 'Add New Pincode'}
          open={modalVisible}
          onCancel={() => {
            setModalVisible(false);
            setEditingPincode(null);
            form.resetFields();
          }}
          footer={null}
          width={400}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{
              deliveryAvailable: true,
              deliveryDays: '1'
            }}
          >
            <Form.Item
              name="pincode"
              label="Pincode"
              rules={[
                { required: true, message: 'Please enter pincode' },
                { pattern: /^\d{6}$/, message: 'Must be exactly 6 digits' }
              ]}
            >
              <Input 
                placeholder="Enter 6-digit pincode" 
                maxLength={6}
                type="text"
              />
            </Form.Item>

            <Form.Item
              name="city"
              label="City"
              rules={[
                { required: true, message: 'Please enter city name' }
              ]}
            >
              <Input placeholder="Enter city name" />
            </Form.Item>

            <Form.Item
              name="deliveryAvailable"
              label="Delivery Available"
              valuePropName="checked"
              rules={[{ required: true, message: 'Required' }]}
            >
              <Switch checkedChildren="Yes" unCheckedChildren="No" defaultChecked />
            </Form.Item>

            <Form.Item
              name="deliveryDays"
              label="Delivery Days"
              rules={[{ required: true, message: 'Please select delivery days' }]}
            >
              <Select placeholder="Select delivery days">
                <Option value="same">Same Day</Option>
                <Option value="0">0 Days</Option>
                <Option value="1">1 Day</Option>
                <Option value="2">2 Days</Option>
                <Option value="3">3 Days</Option>
                <Option value="4">4 Days</Option>
                <Option value="5">5 Days</Option>
                <Option value="6">6 Days</Option>
                <Option value="7">7 Days</Option>
              </Select>
            </Form.Item>

            <Form.Item className="mb-0">
              <div className="flex justify-end gap-3">
                <Button onClick={() => setModalVisible(false)}>
                  Cancel
                </Button>
                <Button type="primary" htmlType="submit" className="bg-blue-600 hover:bg-blue-700">
                  {editingPincode ? 'Update' : 'Create'}
                </Button>
              </div>
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </div>
  );
};

export default PincodeManagement;